import {
  Box,
  Grid,
  Typography,
  Slider,
  Paper,
  Button,
  Container,
} from "@mui/material";
import { useState, useRef } from "react";
import {
  EmojiObjects,
  Speed,
  CheckCircle,
  SolarPower,
  Bolt,
  Cloud,
  Layers,
  Build,
  BugReport,
  Factory,
} from "@mui/icons-material";
import AnimatedNumber from "react-animated-number";
import Particles from "react-tsparticles";
import { motion, AnimatePresence } from "framer-motion";

/* ------------------ COLORS ------------------ */
const COLORS = {
  blue: "#0b3c8c",
  yellow: "#ffd600",
  light: "#f5f7fa",
};

/* ------------------ SHIMMER CARD ------------------ */
const ShimmerCard = ({ children, bg = COLORS.blue, color = "#fff", onClick }) => (
  <Paper
    elevation={3}
    onClick={onClick}
    sx={{
      p: 3,
      borderRadius: 3,
      backgroundColor: bg,
      color,
      textAlign: "center",
      height: "100%",
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.3s",
      "&:hover": onClick ? { transform: "translateY(-4px)" } : {},
    }}
  >
    {children}
  </Paper>
);

/* ------------------ METRIC CARD ------------------ */
const MetricCard = ({
  value,
  label,
  suffix = "",
  bg = COLORS.blue,
  color = "#fff",
}) => (
  <ShimmerCard bg={bg} color={color}>
    <AnimatePresence mode="wait">
      <motion.div
        key={value}
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -15 }}
        transition={{ duration: 0.4 }}
      >
        <AnimatedNumber
          value={value}
          duration={900}
          formatValue={(n) =>
            `${Intl.NumberFormat("bg-BG").format(n)}${suffix}`
          }
          style={{ fontSize: 32, fontWeight: "bold" }}
        />
      </motion.div>
    </AnimatePresence>
    <Typography mt={1} fontSize={14}>
      {label}
    </Typography>
  </ShimmerCard>
);

/* ------------------ MAIN COMPONENT ------------------ */
export default function SolarROILandingFull() {
  const [mwp, setMwp] = useState(10);
  const [price, setPrice] = useState(120);
  const calculatorRef = useRef(null);

  const contaminationTypes = [
    { type: "Прах и прахови частици", loss: 10, cleaningCostPerMWp: 190, icon: <Cloud fontSize="large" /> },
    { type: "Смесени замърсявания", loss: 12, cleaningCostPerMWp: 190, icon: <Layers fontSize="large" /> },
    { type: "Ръжда, метални оксиди", loss: 18, cleaningCostPerMWp: 2000, icon: <Build fontSize="large" /> },
    { type: "Екскременти от птици", loss: 20, cleaningCostPerMWp: 380, icon: <BugReport fontSize="large" /> },
    { type: "Циментов прах", loss: 40, cleaningCostPerMWp: 3500, icon: <Factory fontSize="large" /> },
  ];

  const [selectedContamination, setSelectedContamination] = useState(contaminationTypes[0]);
  const productionPerMWp = 1350;

  const annualRevenue = mwp * productionPerMWp * price;
  const annualLoss = annualRevenue * (selectedContamination.loss / 100);
  const cleaningCostTotal = mwp * selectedContamination.cleaningCostPerMWp;
  const roiMonths = (cleaningCostTotal / annualLoss) * 12;

  const scrollToCalculator = () =>
    calculatorRef.current?.scrollIntoView({ behavior: "smooth" });

  const benefits = [
    {
      icon: <EmojiObjects fontSize="large" sx={{ mb: 2, color: COLORS.yellow }} />,
      title: "Повишаване на производителността",
      desc: "Редовното почистване увеличава енергийния добив до 20%, като премахва натрупания прах и отлагания.",
    },
    {
      icon: <Speed fontSize="large" sx={{ mb: 2, color: COLORS.yellow }} />,
      title: "По-дълъг живот на панелите",
      desc: "Премахването на замърсявания предотвратява микропукнатини и корозия, удължавайки живота на вашата централа.",
    },
    {
      icon: <CheckCircle fontSize="large" sx={{ mb: 2, color: COLORS.yellow }} />,
      title: "Бърза възвръщаемост",
      desc: "Инвестицията в поддръжка се връща за няколко месеца чрез увеличена продукция и намалени загуби.",
    },
    {
      icon: <Bolt fontSize="large" sx={{ mb: 2, color: COLORS.yellow }} />,
      title: "Намаляване на загубите",
      desc: "Премахването на органични и химични замърсявания минимизира загубата на енергия.",
    },
  ];

  return (
    <Box sx={{ position: "relative", overflow: "hidden" }}>
      {/* Hero */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0b3c8c, #4a90e2)",
          color: "#fff",
          py: 20,
          textAlign: "center",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-50%",
            left: "-50%",
            width: "200%",
            height: "200%",
            background: "radial-gradient(circle, rgba(255,214,0,0.15) 0%, transparent 70%)",
            animation: "rotateSun 40s linear infinite",
            zIndex: 0,
            borderRadius: "50%",
          },
          "@keyframes rotateSun": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
        }}
      >
        <Particles id="tsparticles" options={{ background: { color: "transparent" }, fpsLimit: 60, interactivity: { events: { onHover: { enable: false }, onClick: { enable: false } } }, particles: { color: { value: COLORS.yellow }, links: { enable: false }, move: { enable: true, speed: 0.3, direction: "top", outModes: "out" }, number: { value: 70 }, opacity: { value: 0.3 }, size: { value: { min: 1, max: 4 } } } }} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
        <SolarPower sx={{ fontSize: 120, color: COLORS.yellow, opacity: 0.15, position: "absolute", top: 40, left: "50%", transform: "translateX(-50%)", zIndex: 0 }} />
        <Typography variant="h3" fontWeight="bold" mb={2} sx={{ position: "relative", zIndex: 1 }}>
          Изчисли възвръщаемост на инвестиция "ROI" от почистване на соларната си централа
        </Typography>
        <Typography variant="h6" mb={4} sx={{ position: "relative", zIndex: 1 }}>
          Разбери колко бързо се връща инвестицията в поддръжка и защита на панелите ти
        </Typography>
        <Button size="large" sx={{ backgroundColor: COLORS.yellow, color: COLORS.blue, fontWeight: "bold", px: 6, py: 1.5, fontSize: "1rem", "&:hover": { backgroundColor: "#ffe000", transform: "scale(1.05)" }, position: "relative", zIndex: 1 }} onClick={scrollToCalculator}>
          Изчисли сега
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 10, position: "relative", zIndex: 2 }} ref={calculatorRef}>
        {/* Contamination Types */}
        <Box mb={10} textAlign="center">
          <Typography variant="h5" fontWeight="bold" mb={5}>
            Влияние на различните замърсявания
          </Typography>
           <Typography variant="h6" fontStyle="italic" mb={5}>
            Моля, изберете тип замърсяване.
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {contaminationTypes.map((item, index) => {
              const active = selectedContamination.type === item.type;
              return (
                <Grid item xs={12} md={2} key={index} display="flex">
                  <motion.div layout whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} style={{ flex: 1 }}>
                    <Paper
                      onClick={() => setSelectedContamination(item)}
                      elevation={active ? 8 : 2}
                      sx={{
                        height: "100%",
                        p: 3,
                        borderRadius: 3,
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: active ? COLORS.blue : COLORS.light,
                        color: active ? "#fff" : COLORS.blue,
                        border: `2px solid ${active ? COLORS.yellow : "#e0e0e0"}`,
                      }}
                    >
                      <Box sx={{ color: active ? COLORS.yellow : COLORS.blue, mb: 1 }}>{item.icon}</Box>
                      <Typography fontWeight="bold" mb={1}>{item.type}</Typography>
                      <Typography fontSize={16} color={COLORS.yellow}>{item.loss}%</Typography>
                      <Typography fontSize={12} mt={1} color="text.inherit">загуба на производство</Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* ROI Calculator */}
        <Box mb={10}>
          <Grid container spacing={5} mb={6}>
            <Grid item xs={12} md={6}>
              <Typography fontWeight="bold">Мощност: {mwp} MWp</Typography>
              <Slider min={1} max={200} value={mwp} onChange={(e, v) => setMwp(v)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography fontWeight="bold">Цена на електроенергия: {price} €/MWh</Typography>
              <Slider min={80} max={200} step={5} value={price} onChange={(e, v) => setPrice(v)} />
            </Grid>
          </Grid>

          {/* Metrics */}
          <Grid container spacing={4} mb={6}>
            <Grid item xs={12} md={3}>
              <MetricCard value={annualRevenue.toFixed(0)} suffix="€" label="Годишен оборот" />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCard value={annualLoss.toFixed(0)} suffix="€" label="Годишни загуби" />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCard value={cleaningCostTotal.toFixed(0)} suffix="€" label="Цена на почистване" bg={COLORS.yellow} color={COLORS.blue} />
            </Grid>
            <Grid item xs={12} md={3}>
              <MetricCard value={roiMonths.toFixed(1)} suffix=" м." label="ROI (месеца)" bg={COLORS.yellow} color={COLORS.blue} />
            </Grid>
          </Grid>
        </Box>

        {/* Benefits Section */}
        <Box textAlign="center" mb={10}>
          <Typography variant="h5" fontWeight="bold" mb={5}>
            Защо е важно да се почистват соларните панели
          </Typography>
          <Grid container spacing={5}>
            {benefits.map((item, index) => (
              <Grid item xs={12} md={3} key={index}>
                <ShimmerCard>
                  {item.icon}
                  <Typography fontWeight="bold" mb={1}>{item.title}</Typography>
                  <Typography fontSize={14}>{item.desc}</Typography>
                </ShimmerCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
